import * as React from 'react'
import { CornerDownRight, MessageSquare, Pencil, Radio, Send, Trash2, WifiOff, X } from 'lucide-react'
import { sb } from '@/lib/supabase'
import { useAuth } from '@/auth/auth-provider'
import { useQuery } from '@/hooks/use-query'
import { usePoll, useRealtime } from '@/hooks/use-realtime'
import { usePresence } from '@/hooks/use-presence'
import { displayName, initials, relativeTime } from '@/lib/format'
import type { DiscussionMessage, DiscussionReaction, Profile } from '@/lib/types'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Card, CardContent } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { EmptyState, ErrorNote, Loading, Page } from '@/components/page'

const REACTIONS = ['👍', '🎉', '👀', '🤔', '❤️']

interface Thread {
  message: DiscussionMessage
  replies: DiscussionMessage[]
}

export function DiscussionPage() {
  const { currentTeam, session } = useAuth()
  const teamId = currentTeam?.id
  const userId = session?.user?.id
  const [replyTo, setReplyTo] = React.useState<string | null>(null)
  const [editing, setEditing] = React.useState<DiscussionMessage | null>(null)
  const bottomRef = React.useRef<HTMLDivElement>(null)

  const { data, loading, error, refetch } = useQuery(async () => {
    if (!teamId) return { messages: [] as DiscussionMessage[], reactions: [] as DiscussionReaction[], profiles: [] as Profile[] }
    const [msgs, reacts, profs] = await Promise.all([
      sb().from('discussion_messages').select('*').eq('team_id', teamId).order('created_at'),
      sb().from('discussion_reactions').select('*').eq('team_id', teamId),
      sb().from('profiles').select('*'),
    ])
    if (msgs.error) throw msgs.error
    if (reacts.error) throw reacts.error
    if (profs.error) throw profs.error
    return {
      messages: (msgs.data ?? []) as DiscussionMessage[],
      reactions: (reacts.data ?? []) as DiscussionReaction[],
      profiles: (profs.data ?? []) as Profile[],
    }
  }, [teamId])

  // Realtime is the fast path; the poll below is the safety net. A conversation
  // that silently stops updating is worse than one that lags a few seconds.
  const liveMessages = useRealtime<DiscussionMessage>(
    {
      channel: `discussion-${teamId ?? 'none'}`,
      table: 'discussion_messages',
      filter: teamId ? `team_id=eq.${teamId}` : undefined,
      enabled: Boolean(teamId),
    },
    () => void refetch(),
  )
  const liveReactions = useRealtime<DiscussionReaction>(
    {
      channel: `discussion-reactions-${teamId ?? 'none'}`,
      table: 'discussion_reactions',
      filter: teamId ? `team_id=eq.${teamId}` : undefined,
      enabled: Boolean(teamId),
    },
    () => void refetch(),
  )
  // Both channels have to have PROVEN themselves before the poll backs off —
  // joining isn't proof. See the note in use-realtime.ts: this page used to
  // report Live on a subscription that delivered nothing and then poll every 30s,
  // which was slower than admitting it was polling.
  const cdcLive = liveMessages.live && liveReactions.live
  const subscribed = liveMessages.subscribed && liveReactions.subscribed

  // ---- The peer signal ------------------------------------------------------
  //
  // Postgres Changes above has never delivered a row on this platform and, on
  // the evidence, isn't about to: there is no `wal2json`, and installing it was
  // measured as necessary-but-not-sufficient. Broadcast is dead here too — even
  // a self-echo with ack times out. **Presence is the one mechanism that
  // works**, cross-client in single-digit milliseconds
  // (`scripts/presence-check.mjs`).
  //
  // So the page stops waiting for the database to tell it something changed and
  // lets the person who changed it say so instead: every write bumps this
  // client's presence state, and every other client refetches the moment that
  // lands. Presence carries no payload beyond a timestamp — the refetch is what
  // fetches the truth — so nothing here can serve a stale or forged message.
  //
  // **What it cannot cover, which is why the poll stays:** a write made outside
  // this page — the MCP server, `team.sh`, psql — bumps nobody's presence and
  // arrives only on the next poll. That's the honest limit of a peer signal, and
  // it's why "live" below still doesn't mean "the database pushes to us".
  const [myBump, setMyBump] = React.useState(0)
  const myBumpRef = React.useRef(0)
  myBumpRef.current = myBump
  const signal = React.useCallback(() => setMyBump(Date.now()), [])

  const presenceState = React.useMemo(() => ({ bump: myBump }), [myBump])
  const peerSignal = usePresence<{ bump: number }>({
    channel: `discussion-peers-${teamId ?? 'none'}`,
    key: userId ?? 'anon',
    state: presenceState,
    enabled: Boolean(teamId && userId),
  })

  const latestBump = peerSignal.peers.reduce((max, p) => Math.max(max, p.bump ?? 0), 0)
  const seenBump = React.useRef(0)
  React.useEffect(() => {
    if (latestBump <= seenBump.current) return
    seenBump.current = latestBump
    // Our own write already refetched on its way out; refetching again would be
    // a wasted round trip on every message the user sends. A SECOND TAB of the
    // same person stamps a different millisecond, so it still comes through —
    // which is why this compares values rather than presence keys.
    if (latestBump === myBumpRef.current) return
    // Refetch only. Signalling here would be a ping-pong: their bump makes us
    // bump, which makes them refetch and bump, for as long as both tabs are
    // open. A signal is raised by a WRITE, never by hearing about one.
    void refetch()
  }, [latestBump, refetch])

  // Live by either route. The poll behind a peer signal is slower than the raw
  // 4s but faster than the 30s a real CDC feed would earn, because the peer
  // signal has a known blind spot and the database push wouldn't.
  const live = cdcLive || peerSignal.live
  usePoll(() => void refetch(), cdcLive ? 30_000 : peerSignal.live ? 15_000 : 4_000, Boolean(teamId))

  const threads = React.useMemo<Thread[]>(() => {
    const msgs = data?.messages ?? []
    const tops = msgs.filter((m) => !m.parent_id)
    return tops.map((m) => ({
      message: m,
      replies: msgs.filter((r) => r.parent_id === m.id),
    }))
  }, [data?.messages])

  const profileFor = React.useCallback(
    (uid: string) => data?.profiles.find((p) => p.user_id === uid) ?? null,
    [data?.profiles],
  )

  async function post(body: string, parentId: string | null) {
    if (!teamId) return
    const { error: err } = await sb()
      .from('discussion_messages')
      .insert({ team_id: teamId, body, parent_id: parentId })
    if (err) throw err
    setReplyTo(null)
    await refetch()
    signal()
    // Only chase the bottom for a new top-level post; jumping the page while
    // someone reads a thread they just replied to is disorienting.
    if (!parentId) requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }))
  }

  async function saveEdit(message: DiscussionMessage, body: string) {
    const { error: err } = await sb()
      .from('discussion_messages')
      .update({ body, edited_at: new Date().toISOString() })
      .eq('id', message.id)
    if (err) return window.alert(err.message)
    setEditing(null)
    void refetch()
    signal()
  }

  async function softDelete(message: DiscussionMessage) {
    if (!window.confirm('Delete this message?')) return
    // Tombstone rather than DELETE: a hard delete would cascade to replies and
    // remove other people's words along with your own.
    const { error: err } = await sb()
      .from('discussion_messages')
      .update({ deleted_at: new Date().toISOString(), body: '[deleted]' })
      .eq('id', message.id)
    if (err) return window.alert(err.message)
    void refetch()
    signal()
  }

  async function toggleReaction(message: DiscussionMessage, emoji: string) {
    if (!userId) return
    const existing = (data?.reactions ?? []).find(
      (r) => r.message_id === message.id && r.user_id === userId && r.emoji === emoji,
    )
    if (existing) {
      await sb().from('discussion_reactions').delete().eq('id', existing.id)
    } else {
      await sb().from('discussion_reactions').insert({ message_id: message.id, team_id: message.team_id, emoji })
    }
    void refetch()
    signal()
  }

  return (
    <Page
      title="Team Conversation"
      description="One thread the whole team shares."
      actions={
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs',
                live ? 'text-muted-foreground' : 'border-warning/40 text-warning',
              )}
            >
              {live ? <Radio className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
              {live ? 'Live' : 'Polling'}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {/* Three states, not two. "Connected but delivering nothing" is the
                one this tenant is actually in, and calling it plain
                "unavailable" sent someone looking at the wrong layer. */}
            {cdcLive
              ? 'Connected — the database pushes every change here as it happens.'
              : peerSignal.live
                ? // Deliberately not "arrive instantly" full stop. Anything written
                  // outside this page has no one to raise the signal, so the poll
                  // is still what catches it — and the tooltip says so rather than
                  // promising more than the mechanism delivers.
                  'Connected to your teammates — anything posted in the portal appears straight away. Changes made outside it are picked up within 15 seconds.'
                : subscribed
                  ? 'Connected to realtime, but no changes have come through it yet — refreshing every few seconds until one does.'
                  : 'Realtime is unavailable, so this is refreshing every few seconds instead.'}
          </TooltipContent>
        </Tooltip>
      }
    >
      {error && <ErrorNote error={error} />}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)]">
        {loading && !data ? (
          <Loading />
        ) : threads.length === 0 ? (
          <EmptyState
            icon={MessageSquare}
            title="Nothing here yet"
            description="Ask a question, share a link, or post what you're stuck on."
          />
        ) : (
          <div className="space-y-3">
            {threads.map(({ message, replies }) => (
              <Card key={message.id}>
                <CardContent className="p-4">
                  <MessageRow
                    message={message}
                    profile={profileFor(message.user_id)}
                    isMine={message.user_id === userId}
                    reactions={(data?.reactions ?? []).filter((r) => r.message_id === message.id)}
                    userId={userId}
                    editing={editing?.id === message.id}
                    onEdit={() => setEditing(message)}
                    onCancelEdit={() => setEditing(null)}
                    onSaveEdit={(body) => void saveEdit(message, body)}
                    onDelete={() => void softDelete(message)}
                    onReact={(emoji) => void toggleReaction(message, emoji)}
                  />

                  {replies.length > 0 && (
                    <div className="mt-3 space-y-3 border-l-2 pl-4">
                      {replies.map((r) => (
                        <MessageRow
                          key={r.id}
                          message={r}
                          profile={profileFor(r.user_id)}
                          isMine={r.user_id === userId}
                          reactions={(data?.reactions ?? []).filter((x) => x.message_id === r.id)}
                          userId={userId}
                          compact
                          editing={editing?.id === r.id}
                          onEdit={() => setEditing(r)}
                          onCancelEdit={() => setEditing(null)}
                          onSaveEdit={(body) => void saveEdit(r, body)}
                          onDelete={() => void softDelete(r)}
                          onReact={(emoji) => void toggleReaction(r, emoji)}
                        />
                      ))}
                    </div>
                  )}

                  {replyTo === message.id ? (
                    <div className="mt-3 border-l-2 pl-4">
                      <Composer
                        placeholder="Reply…"
                        autoFocus
                        submitLabel="Reply"
                        onCancel={() => setReplyTo(null)}
                        onSubmit={(body) => post(body, message.id)}
                      />
                    </div>
                  ) : (
                    !message.deleted_at && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 -ml-2 text-muted-foreground"
                        onClick={() => setReplyTo(message.id)}
                      >
                        <CornerDownRight /> Reply
                      </Button>
                    )
                  )}
                </CardContent>
              </Card>
            ))}
            <div ref={bottomRef} />
          </div>
        )}

        <Card className="sticky bottom-4 shadow-md">
          <CardContent className="p-3">
            <Composer placeholder="Say something to the team…" submitLabel="Post" onSubmit={(body) => post(body, null)} />
          </CardContent>
        </Card>
      </div>
    </Page>
  )
}

function MessageRow({
  message,
  profile,
  isMine,
  reactions,
  userId,
  compact,
  editing,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
  onReact,
}: {
  message: DiscussionMessage
  profile: Profile | null
  isMine: boolean
  reactions: DiscussionReaction[]
  userId: string | undefined
  compact?: boolean
  editing: boolean
  onEdit: () => void
  onCancelEdit: () => void
  onSaveEdit: (body: string) => void
  onDelete: () => void
  onReact: (emoji: string) => void
}) {
  const deleted = Boolean(message.deleted_at)

  const grouped = React.useMemo(() => {
    const m = new Map<string, { count: number; mine: boolean }>()
    for (const r of reactions) {
      const cur = m.get(r.emoji) ?? { count: 0, mine: false }
      m.set(r.emoji, { count: cur.count + 1, mine: cur.mine || r.user_id === userId })
    }
    return [...m.entries()]
  }, [reactions, userId])

  return (
    <div className="group flex gap-3">
      <Avatar className={cn('shrink-0', compact ? 'h-7 w-7' : 'h-9 w-9')}>
        <AvatarFallback className={compact ? 'text-[10px]' : undefined}>
          {initials(profile?.display_name, profile?.email)}
        </AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <span className={cn('font-medium', compact ? 'text-sm' : 'text-sm')}>
            {displayName(profile)}
          </span>
          <span className="text-xs text-muted-foreground">{relativeTime(message.created_at)}</span>
          {message.edited_at && !deleted && (
            <span className="text-xs text-muted-foreground">(edited)</span>
          )}

          {isMine && !deleted && !editing && (
            <span className="ml-auto flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
              <Button variant="ghost" size="icon-sm" onClick={onEdit} aria-label="Edit message">
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon-sm" onClick={onDelete} aria-label="Delete message">
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </span>
          )}
        </div>

        {editing ? (
          <div className="mt-1.5">
            <Composer
              initial={message.body}
              autoFocus
              submitLabel="Save"
              onCancel={onCancelEdit}
              onSubmit={async (body) => onSaveEdit(body)}
            />
          </div>
        ) : (
          <p
            className={cn(
              'mt-0.5 whitespace-pre-wrap break-words text-sm',
              deleted && 'italic text-muted-foreground',
            )}
          >
            {deleted ? 'Message deleted' : message.body}
          </p>
        )}

        {!deleted && !editing && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            {grouped.map(([emoji, { count, mine }]) => (
              <button
                key={emoji}
                onClick={() => onReact(emoji)}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition-colors',
                  mine ? 'border-primary/50 bg-primary/10' : 'hover:bg-accent',
                )}
                aria-pressed={mine}
              >
                <span>{emoji}</span>
                <span className="tabular-nums text-muted-foreground">{count}</span>
              </button>
            ))}
            <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
              {REACTIONS.filter((e) => !grouped.some(([g]) => g === e)).map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => onReact(emoji)}
                  className="rounded-full px-1.5 py-0.5 text-xs opacity-60 hover:bg-accent hover:opacity-100"
                  aria-label={`React with ${emoji}`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Composer({
  initial = '',
  placeholder,
  submitLabel,
  autoFocus,
  onSubmit,
  onCancel,
}: {
  initial?: string
  placeholder?: string
  submitLabel: string
  autoFocus?: boolean
  onSubmit: (body: string) => Promise<void>
  onCancel?: () => void
}) {
  const [value, setValue] = React.useState(initial)
  const [busy, setBusy] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  async function submit() {
    const body = value.trim()
    if (!body) return
    setBusy(true)
    setError(null)
    try {
      await onSubmit(body)
      setValue('')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <Textarea
        rows={2}
        autoFocus={autoFocus}
        value={value}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          // Enter sends; Shift+Enter makes a new line — the convention every
          // chat UI has trained people to expect.
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            void submit()
          }
          if (e.key === 'Escape' && onCancel) onCancel()
        }}
        className="min-h-0 resize-y"
      />
      {error && <p className="mt-1.5 text-xs text-destructive">{error}</p>}
      <div className="mt-2 flex items-center justify-between">
        <span className="text-[11px] text-muted-foreground">
          Enter to send · Shift+Enter for a new line
        </span>
        <div className="flex gap-1.5">
          {onCancel && (
            <Button variant="ghost" size="sm" onClick={onCancel}>
              <X /> Cancel
            </Button>
          )}
          <Button size="sm" onClick={() => void submit()} disabled={busy || !value.trim()}>
            <Send /> {submitLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
